// PLAYHEAD-TRUE paused canvas synthesis (video only), shared by the desktop stage
// (ComposerCanvasMedia) and the mobile stage (MobileVideoEditor). The editor view holds EVERY
// clip whose window touches the active beat — painting them all at once doubles text the viewer
// never sees together. Gate each layer to its clip's window at the playhead (live while dragging
// a clip on the timeline), except the layer being selected/edited — editing must never go blind.
// Layers NOT visible at the current playhead also get their hit-targets disabled: two hook texts
// sharing the same box (adjacent beats' overlap window) would otherwise route every canvas
// click/double-click to the INVISIBLE top layer, making the visible one unselectable.
// Content edits still flow through editor.comp; this is display-only synthesis.

import type { PostComposition } from '../types'
import type { CompositionEditor } from './use-composition-editor'
import type { VideoTimelineContextValue } from './CompositionEditorProvider'

export function playheadTrueView(
  editor: Pick<CompositionEditor, 'comp' | 'selectedId' | 'editingId'>,
  timeline: Pick<VideoTimelineContextValue, 'spans' | 'activeIndex' | 'clips'>,
  // The live playhead (seconds) — passed in by the caller's useReelPlayhead subscription
  // (the shared context no longer carries a 60fps number).
  currentTime: number
): { displayComp: PostComposition; beatTime?: number; hitDisabledIds?: Set<string> } {
  const span = timeline.spans[timeline.activeIndex]
  if (!span) return { displayComp: editor.comp }
  const beatTime = Math.max(0, Math.min(currentTime - span.start, span.end - span.start))
  const byId = new Map(timeline.clips.map(c => [c.layer.id, c]))
  const hitDisabledIds = new Set<string>()
  const displayComp: PostComposition = {
    ...editor.comp,
    layers: editor.comp.layers.map(l => {
      if (l.id === editor.selectedId || l.id === editor.editingId) return l
      const c = byId.get(l.id)
      if (!c) return l
      const timing = { start: c.start - span.start, end: c.end - span.start }
      if (beatTime < timing.start - 1e-6 || beatTime >= timing.end) hitDisabledIds.add(l.id)
      return { ...l, timing }
    }),
  }
  return { displayComp, beatTime, hitDisabledIds }
}
